// Filing a capture: describe -> plan -> PUT -> commit.
//
// The bytes go from this browser STRAIGHT TO S3 against presigned URLs. Nothing
// we run ever holds a photograph, which is the argument #84 made for Quick Scan
// and which matters more here: a shelf walk can push a hundred images through in
// a few minutes, and buffering those through Node on a t3.medium is how the old
// multipart path held every scan in memory three times over.
//
// ---------------------------------------------------------------------------
// EVERY CALL HERE IS SAME-ORIGIN, AND CARRIES A COOKIE
// ---------------------------------------------------------------------------
// This page is static files. There is no server of ours on this host to hold a
// credential, so the handset's own cookie is the credential, and the backend
// resolves it to a shop by reading the database. Caddy proxies /api on this host
// to the backend, which is what keeps these requests same-origin: the cookie is
// host-only and SameSite=Strict, and both of those depend on never talking to
// another origin. Do not "simplify" this to an absolute backend URL.

import { hashBlob } from "./imageBytes";
import { tracedFetch } from "./tracedFetch";
import { mark, span } from "./stepLog";

// Same-origin, proxied to the backend by Caddy on this host. Relative on
// purpose — see the header.
const API = "/api/capture";

/**
 * How many photographs go up at once.
 *
 * Three, sized against what a capture ACTUALLY weighs rather than what it was
 * once assumed to: measured on production, four photographs came to 1.06 MB, so
 * three in flight is well under a megabyte. Raise this only with a fresh
 * measurement — the number that matters is bytes in flight, not files.
 */
const UPLOAD_CONCURRENCY = 3;

/**
 * How long one photograph's PUT may take before it counts as failed.
 *
 * Sized for the worst uplink worth waiting on rather than the typical one:
 * ~0.9 MB over a poor connection is tens of seconds, and firing early would
 * turn a slow shelf into a failed one. Ninety seconds is past anything that
 * was going to succeed.
 */
const PUT_TIMEOUT_MS = 90_000;

/** The same for our own endpoints, which measured under 100ms in production. */
const API_TIMEOUT_MS = 30_000;

/**
 * A timeout signal, or nothing on a handset too old to have one.
 *
 * ⚠️ `AbortSignal.timeout` is Safari 16 / Chrome 103, which is NEWER than
 * anything else this app relies on — `crypto.randomUUID` above is already
 * guarded for the same reason, and vite targets es2020 with no polyfill.
 *
 * Used bare, it throws a TypeError SYNCHRONOUSLY on an older iPhone. Not just
 * for uploads: `postJson` is also how a handset redeems its link, so the device
 * could not sign in at all. A timeout meant to turn "hangs forever" into
 * "fails" would instead have turned "works" into "cannot start", on exactly the
 * kind of old phone a shop hands to whoever is walking the shelves.
 *
 * Absent it, the request behaves as it did before this was added.
 */
function timeoutSignal(ms) {
  try {
    return typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
      ? AbortSignal.timeout(ms)
      : undefined;
  } catch {
    return undefined;
  }
}

// Mirrors the backend's ALLOWED_MIMETYPES. A type outside this set is refused
// server-side, so it is worth not sending one.
const ALLOWED = new Set(["image/jpeg", "image/png", "image/webp"]);

const DEVICE_KEY = "cosmos.capture.device";

/**
 * A stable id for THIS HANDSET, minted on first use and kept in localStorage.
 *
 * Deliberately not an identity. It answers "how much did this phone get
 * through", and nothing else: anyone can send any value, clearing site data
 * mints a new one, and two people sharing a handset are one device. It must
 * never gate access to anything.
 *
 * Falls back to an in-memory value when storage is unavailable (private
 * browsing, blocked site data) rather than throwing — losing attribution for a
 * session is a monitoring gap; refusing to capture is a lost afternoon.
 */
let memoryDevice = null;
export function deviceId() {
  if (memoryDevice) return memoryDevice;
  const mint = () => randomToken();
  try {
    const existing = window.localStorage.getItem(DEVICE_KEY);
    if (existing && /^[A-Za-z0-9_-]{1,64}$/.test(existing)) {
      memoryDevice = existing;
      return existing;
    }
    const fresh = mint();
    window.localStorage.setItem(DEVICE_KEY, fresh);
    memoryDevice = fresh;
    return fresh;
  } catch {
    memoryDevice = mint();
    return memoryDevice;
  }
}

/**
 * A token safe to interpolate into an S3 key.
 *
 * Hyphens stripped from the UUID: the backend's isSafeToken bounds both the
 * character set and the length, and there is no reason to spend four of the 64
 * characters on separators that carry nothing.
 */
function randomToken() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, "");
    }
  } catch {
    /* fall through */
  }
  return (
    Math.random().toString(36).slice(2) + Date.now().toString(36)
  ).slice(0, 32);
}

/**
 * An id for ONE capture, to be reused across every retry of it.
 *
 * Exported so the queue can mint it when a capture is handed over rather than
 * when an attempt starts. That is the whole of what makes a retry idempotent:
 * same id, same content-addressed keys, same upserted row.
 */
export function newUploadId() {
  return randomToken();
}

/** Thrown when the backend says the session is gone. The app shows the door. */
export class SessionExpired extends Error {
  constructor() {
    super("session_expired");
    this.name = "SessionExpired";
  }
}

async function postJson(path, body) {
  const res = await tracedFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    // Same reasoning as the PUT: a request that hangs forever is worse than one
    // that fails, because only a failure can be retried.
    signal: timeoutSignal(API_TIMEOUT_MS),
  });
  if (res.status === 401) throw new SessionExpired();
  const text = await res.text();
  let parsed = null;
  try {
    parsed = JSON.parse(text);
  } catch {
    // Non-JSON from /api/capture almost always means Caddy answered instead of
    // the backend — the proxy is misconfigured, or the backend is down and the
    // static host served its own error page. Say that, rather than reporting
    // "unexpected token <".
    throw new Error(
      `${path} did not return JSON (status ${res.status}). ` +
        `Check that /api is proxied to the backend on this host.`,
    );
  }
  if (!res.ok) throw Object.assign(new Error(parsed?.error || `request_failed_${res.status}`), { status: res.status });
  return parsed;
}

/**
 * The shop this handset is filing for, or null if the session is dead.
 * `productId`/`productTitle` are set when the grant is bound to one product —
 * the ONLY place the app learns that; the `p` in the link is a hint.
 */
// index.html sends the first session check before the app's script has loaded.
// One use, and only within a few seconds: later checks must be fresh.
async function earlySession() {
  const e = typeof window !== "undefined" ? window.__capSession : null;
  if (!e) return null;
  window.__capSession = null;
  if (performance.now() - e.at > 10_000) return null;
  try {
    const res = await e.res;
    const ms = performance.now() - e.at;
    mark("call", { method: "GET", path: `${API}/session`, status: res.status, ok: res.ok, ms, early: true });
    return res;
  } catch {
    return null;
  }
}

export async function fetchSession() {
  const end = span("session_checked");
  const res = (await earlySession()) || (await tracedFetch(`${API}/session`));
  end({ status: res.status });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`session_failed_${res.status}`);
  return res.json();
}

/**
 * The phone's model ("Pixel 8"), the one part of its name the User-Agent
 * header cannot give the server. Display only, never trusted: the server
 * sanitises it and reads the generic model and the browser from the UA itself.
 */
export async function deviceHint() {
  try {
    const high = await navigator.userAgentData?.getHighEntropyValues?.(["model"]);
    return high?.model ? { model: high.model } : null;
  } catch {
    // Not offered or refused: the server's User-Agent parse is enough.
    return null;
  }
}

/** Exchange a handoff token for a session cookie. Single use, server-side. */
export async function enterWithToken(token) {
  const end = span("link_redeem");
  const hint = await deviceHint().catch(() => null);
  try {
    const out = await postJson(`${API}/enter`, { token, deviceId: deviceId(), deviceHint: hint });
    end({ ok: 1 });
    return out;
  } catch (err) {
    end({ ok: 0, why: err?.message });
    throw err;
  }
}

/** Sign this handset out, revoking the grant rather than merely forgetting it. */
export async function leaveSession() {
  await tracedFetch(`${API}/leave`, { method: "POST" });
}

/** Tell the desktop this Bulk session is finished. Callers treat failure as non-blocking. */
export async function finishSession() {
  return postJson(`${API}/session/finish`, {});
}

/**
 * Who owns this barcode or typed code: `{kind, candidates, noBarcode}`.
 * Throws on any failure; the caller falls back to a barcode-only capture.
 */
export async function identify({ barcode, code }) {
  const qs = barcode
    ? `barcode=${encodeURIComponent(barcode)}`
    : `code=${encodeURIComponent(code || "")}`;
  const res = await tracedFetch(`${API}/identify?${qs}`, { signal: timeoutSignal(API_TIMEOUT_MS) });
  if (res.status === 401) throw new SessionExpired();
  if (!res.ok) throw new Error(`identify_failed_${res.status}`);
  const body = await res.json();
  return {
    kind: body.kind,
    candidates: Array.isArray(body.candidates) ? body.candidates : [],
    noBarcode: Boolean(body.noBarcode),
  };
}

/** Deletes one saved source photo of a product. Throws on any failure. */
export async function removeSavedPhoto({ productId, imageId }) {
  return postJson(`${API}/item-photos/remove`, { productId, imageId });
}

// A retry re-describes the same blobs; hash each once.
const hashes = new WeakMap();
async function cachedHash(blob) {
  if (!hashes.has(blob)) hashes.set(blob, await hashBlob(blob));
  return hashes.get(blob);
}

// Shots uploaded while the merchant is still shooting, keyed by blob.
const staged = new WeakMap();
let stagedInFlight = 0;
const stagedWaiting = [];

/**
 * PUT one shot now, under the deck's id and slot, so Done only has to commit.
 *
 * Never throws and nothing waits on it: a failed or still-running stage is
 * picked up by fileCapture, which re-PUTs whatever is not confirmed. A shot the
 * merchant removes is simply never committed; its object stays uncommitted in S3.
 */
export function stageShot(blob, uploadId, index) {
  if (!blob || staged.has(blob)) return;
  const mimetype = ALLOWED.has(blob.type) ? blob.type : "image/jpeg";
  const rec = { uploadId, index, mimetype, size: blob.size, hash: null, key: null, ok: false };
  rec.done = (async () => {
    // Same cap as the commit-time PUTs; the rest wait their turn.
    if (stagedInFlight >= UPLOAD_CONCURRENCY) await new Promise((r) => stagedWaiting.push(r));
    stagedInFlight += 1;
    let end = null;
    let why = "";
    try {
      rec.hash = await cachedHash(blob);
      end = span("stage_upload");
      const plan = await postJson(`${API}/upload-url`, {
        uploadId,
        photos: [{ index, hash: rec.hash, mimetype, size: blob.size }],
      });
      const p = plan.photos[0];
      const res = await tracedFetch(p.url, {
        method: "PUT",
        headers: { "Content-Type": p.mimetype },
        body: blob,
        signal: timeoutSignal(PUT_TIMEOUT_MS),
      });
      if (res.ok) {
        rec.key = p.key;
        rec.ok = true;
      } else why = `put_${res.status}`;
    } catch (err) {
      why = err?.message || err?.name;
      /* fileCapture uploads it at commit */
    } finally {
      end?.({ idx: index, bytes: blob.size, ok: rec.ok, why });
      if (!rec.ok) mark("upload_error", { where: "stage", idx: index, why });
      stagedInFlight -= 1;
      stagedWaiting.shift()?.();
    }
  })();
  staged.set(blob, rec);
}

/**
 * File one product: its barcode and its photographs.
 *
 * `uploadId` is minted HERE, before a single byte moves, and the commit is an
 * upsert on it — so a reply lost on a bad connection can be retried and files
 * the product once, not twice. The same reasoning makes the S3 keys
 * content-addressed: re-uploading identical bytes writes to the identical key.
 *
 * `productBound` is the caller saying the SESSION is bound to a product, so
 * the backend files against the grant's product and needs no barcode. A shelf
 * capture still refuses an empty one here.
 *
 * `uploaded` is the caller's Set of S3 keys whose PUT already succeeded in an
 * earlier attempt of this capture; those are skipped, and it is filled as PUTs land.
 *
 * `recapture` marks a guided recapture, so the server rechecks the photos after filing.
 * `target` is `{productId}` or `{newItem: true}`; with none, the barcode alone
 * decides (find or mint). A target stands in for a barcode.
 *
 * @param {{barcode?: string|null, target?: {productId?: string, newItem?: boolean}|null, shots: Array<{blob: Blob}>, onProgress?: (done: number, total: number) => void, uploadId?: string, productBound?: boolean, uploaded?: Set<string>}} args
 * @returns {Promise<{stored: number, device: number|null, total: number, productId?: string, code?: string|null, barcode?: string|null}>}
 */
export async function fileCapture({ barcode, target = null, recapture = false, shots, onProgress, uploadId: given, productBound = false, uploaded = new Set() }) {
  if (!barcode && !productBound && !target) throw new Error("barcode_required");
  if (!Array.isArray(shots) || shots.length === 0) throw new Error("no_photos");

  // THE ID COMES FROM THE CALLER, AND THE RETRY DEPENDS ON THAT.
  //
  // This used to mint one here, so every retry of the SAME capture uploaded
  // under a fresh id — a fresh S3 prefix — abandoning whatever the previous
  // attempt had managed to PUT. The idempotence this file claims is real only
  // when the id survives the retry, and it did not: krystals-express held 19
  // prefixes in S3 for far fewer captures on 2026-09-11, most of them
  // abandoned attempts rather than distinct products.
  //
  // Still minted here when nobody passes one, so a direct caller keeps working.
  const uploadId = given || randomToken();

  // 0. Shots already staged under this id (see stageShot). Wait for any still
  //    in flight; their slots and keys stand only if every shot was staged,
  //    with slots in deck order. Otherwise they are ignored and all is planned.
  const recs = shots.map((s) => staged.get(s.blob));
  await Promise.all(recs.map((r) => r?.done));
  const early =
    recs.every((r, i) => r?.ok && r.uploadId === uploadId && (i === 0 || r.index > recs[i - 1].index));

  // 1. Describe. The hash is both the integrity check and the content-addressed
  //    part of the key, so it is computed over the exact bytes we will PUT.
  const described = early
    ? recs.map((r) => ({ index: r.index, hash: r.hash, mimetype: r.mimetype, size: r.size }))
    : await Promise.all(
        shots.map(async (shot, index) => ({
          index,
          hash: await cachedHash(shot.blob),
          mimetype: ALLOWED.has(shot.blob.type) ? shot.blob.type : "image/jpeg",
          size: shot.blob.size,
        })),
      );

  // 2. Plan. Returns one presigned PUT per photograph and mints nothing
  //    durable — abandoning here leaves no row, only orphan objects.
  //    Skipped when every shot is already in S3.
  const plan = early
    ? { photos: recs.map((r) => ({ index: r.index, key: r.key, mimetype: r.mimetype })) }
    : await postJson(`${API}/upload-url`, { uploadId, photos: described });
  if (early) recs.forEach((r) => uploaded.add(r.key));

  const keyByIndex = new Map(plan.photos.map((p) => [p.index, p.key]));

  // 3. PUT the bytes, THREE AT A TIME.
  //
  //    This was strictly sequential, on the reasoning that "a phone on shop wifi
  //    pushing eight 2MB images in parallel is how you get a stalled connection
  //    and a timeout on all eight at once". The premise was wrong, and only
  //    production told us: a real four-photograph capture measured 262, 274, 283
  //    and 266 KB — about 1.06 MB in total, not the 8 to 16 MB that reasoning was
  //    sized against. a 3072px q0.9 capture measured 0.66-1.46 MB.
  //
  //    So sequential was paying a full round trip of latency per photograph for
  //    a risk that is not there at this size. Three concurrent is roughly three
  //    times faster on the same connection while keeping under a megabyte in
  //    flight — well inside what a phone on shop wifi carries without stalling.
  //
  //    NOT unbounded. The cap is the point: it is what keeps this true if a
  //    future change raises SHOT_MAX_EDGE or the quality, and it bounds what a
  //    retry storm can put on the wire.
  //    A photo whose key is in `uploaded` is already in S3 (the key is
  //    content-addressed), so a retry sends only what is missing.
  const queue = plan.photos.filter((p) => !uploaded.has(p.key));
  let done = plan.photos.length - queue.length;
  let failure = null;

  async function worker() {
    for (;;) {
      const planned = queue.shift();
      if (!planned || failure) return;
      const shot = shots[described.findIndex((d) => d.index === planned.index)];
      try {
        const res = await tracedFetch(planned.url, {
          method: "PUT",
          // MUST match the type the URL was signed with — it is part of the
          // signature and S3 rejects a mismatch with a bare 403.
          headers: { "Content-Type": planned.mimetype },
          body: shot.blob,
          // A PUT WITH NO TIMEOUT NEVER FAILS. IT JUST NEVER FINISHES.
          //
          // This was a bare fetch. On a handover between wifi and cellular, or
          // a phone locked mid-upload, the request hangs indefinitely: the
          // queue's head stays "running", nothing throws, no retry fires, the
          // item never parks — and so the Retry button, which only appears once
          // something has FAILED, never appears either. The screen reads
          // "Uploading 1 in the background" until the tab dies.
          //
          // Reported as exactly that, and it is the shape of the stranded
          // prefixes with a plan and no objects behind it.
          //
          // Generous rather than tight: a photograph is ~0.7-1.5 MB at 3072px
          // and shop-floor uplink is slow, so this must not fire on a
          // connection that is merely bad. It exists to turn "forever" into
          // "failed", which is a state the queue already knows how to handle.
          signal: timeoutSignal(PUT_TIMEOUT_MS),
        });
        if (!res.ok) {
          throw new Error(
            res.status === 0 || res.type === "opaque"
              ? "upload_blocked"
              : `upload_failed_${res.status}`,
          );
        }
      } catch (err) {
        // FIRST failure wins and stops the rest. Reporting the last one would
        // be arbitrary, and letting the others finish after a failure spends a
        // merchant's connection on bytes we are about to abandon.
        failure = failure || err;
        mark("upload_error", { where: "commit", why: err?.message || err?.name });
        return;
      }
      uploaded.add(planned.key);
      done += 1;
      onProgress?.(done, plan.photos.length);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, worker),
  );
  if (failure) throw failure;

  // 4. Commit. Keys only. The backend re-derives every key and HeadObjects it,
  //    so this is a claim it verifies rather than a fact it accepts.
  const endCommit = span("commit");
  try {
    const out = await postJson(`${API}/commit`, {
      uploadId,
      barcode: barcode || "",
      ...(target ? { target } : {}),
      ...(recapture ? { recapture: true } : {}),
      deviceId: deviceId(),
      photos: described.map((d, i) => ({
        ...d,
        key: keyByIndex.get(d.index),
        ...(shots[i]?.q ? { q: shots[i].q } : {}),
      })),
    });
    endCommit({ ok: 1, n: described.length });
    return out;
  } catch (err) {
    endCommit({ ok: 0, n: described.length, why: err?.message });
    // The backend could not find an object we believed was PUT (it names no
    // key): forget the done-set so the next attempt re-PUTs everything.
    if (err?.message === "photo_not_uploaded") {
      uploaded.clear();
      recs.forEach((r) => r && (r.ok = false));
    }
    throw err;
  }
}
